import * as React from 'react'
import { Theme } from '@mui/material/styles'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CssBaseline from '@mui/material/CssBaseline'
import Drawer from '@mui/material/Drawer'
import Divider from '@mui/material/Divider'
import MenuIcon from '@mui/icons-material/Menu'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Button from '@mui/material/Button'
import { useDispatch, useSelector } from 'react-redux'
import {
  HOME_ROUTE,
  LOGOUT_ROUTE,
  REGISTER_ROUTE,
  UNITS_ROUTE,
} from '../../config'
import { useDimensions } from '../../hooks/useDimensions'
import { ADMIN_EMAIL } from '../../config/getEnvVars'
import { LoadingComponent } from '../../components/loader'
import { onIsLoading } from '../../utils/onLoading'
import { getIsAdmin, getIsAuthenticated, getIsLoading } from '../App/selectors'
import { getNavItems } from './NavItems'
import { NAV_COLOR_PROP, NAV_ICON_PROP, NAV_SX_PROP } from './config'
import { useFirebase } from 'react-redux-firebase'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStateChange } from '../App/hooks/useAuthState'
import { actions as appActions } from '../App/reducer'
import logo from '../../assets/logo.png'
import { CONTENT_MAX_WIDTH } from '../../config'

const drawerWidth = 240

const BrandLockup: React.FC<{
  onClick?: () => void
  logoSize: number
  textVariant: 'caption' | 'body2' | 'h6'
  textSx?: object
}> = ({ onClick, logoSize, textVariant, textSx }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1.25,
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <Box
      component="img"
      src={logo}
      alt="Just Lock It logo"
      sx={{
        width: logoSize,
        height: logoSize,
        objectFit: 'contain',
        filter: 'drop-shadow(0 6px 14px rgba(0, 0, 0, 0.28))',
      }}
    />
    <Typography
      variant={textVariant}
      sx={{
        whiteSpace: 'nowrap',
        flexShrink: 0,
        color: '#f7fbff',
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        lineHeight: 1,
        textShadow: '0 2px 10px rgba(0, 0, 0, 0.24)',
        ...textSx,
      }}
    >
      Just Lock It
    </Typography>
  </Box>
)

export const AppBarComponent: React.FC<{
  fullWidth?: boolean
}> = ({ fullWidth = false }) => {
  const firebase = useFirebase()
  const dispatch = useDispatch()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const isAdminSelector = React.useMemo(() => getIsAdmin(ADMIN_EMAIL), [])
  const isAdmin = useSelector(isAdminSelector)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const location = useLocation()
  const navigate = useNavigate()

  const { width } = useDimensions()
  const isLoading = useSelector(getIsLoading)
  const currentRoute = location.pathname

  const isRegister = currentRoute === REGISTER_ROUTE
  const isSmall = width < 830

  const navItems = getNavItems({ isAdmin, isAuthenticated, isRegister })

  const onDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState)
  }

  const pathname = location.pathname

  const onChangeRoute = React.useCallback(
    async (path: string) => {
      if (path === LOGOUT_ROUTE) {
        await firebase.logout()
        onIsLoading(false)
        dispatch(appActions.onUserInfo(null))
        dispatch(appActions.onCustomerProfileId(undefined))
        dispatch(appActions.onSubscriptionIds(undefined))
        navigate(HOME_ROUTE)
      } else if (path !== pathname) navigate(path)
      if (mobileOpen) onDrawerToggle()
    },
    [pathname, onDrawerToggle, firebase, navigate, dispatch, mobileOpen]
  )

  useAuthStateChange()
  const drawer = (
    <Box
      onClick={onDrawerToggle}
      sx={{
        textAlign: 'center',
        pt: 1,
      }}
    >
      <BrandLockup
        onClick={() => {
          void onChangeRoute(HOME_ROUTE)
        }}
        logoSize={38}
        textVariant="body2"
      />
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.title} disablePadding>
            <ListItemButton
              onClick={async () => await onChangeRoute(item.path)}
              sx={{ textAlign: 'center' }}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        sx={{
          background:
            'linear-gradient(180deg, rgba(15, 18, 23, 0.97) 0%, rgba(10, 13, 18, 0.98) 100%)',
          borderBottom: '1px solid rgba(173, 223, 255, 0.08)',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.28)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <LoadingComponent isLoading={isLoading} />
        <Toolbar
          sx={{
            width: '100%',
            maxWidth: fullWidth ? 'none' : CONTENT_MAX_WIDTH,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            minHeight: { xs: '68px', sm: '72px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, md: 2 },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={onDrawerToggle}
            sx={{
              mr: 1.5,
              width: 44,
              height: 44,
              display: isSmall ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              color: 'rgba(232, 244, 255, 0.92)',
              border: '1px solid rgba(173, 223, 255, 0.14)',
              backgroundColor: 'rgba(22, 28, 36, 0.56)',
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              display: isSmall ? 'flex' : 'none',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <BrandLockup
              onClick={() => {
                void onChangeRoute(HOME_ROUTE)
              }}
              logoSize={42}
              textVariant="body2"
              textSx={{ fontSize: '1rem', letterSpacing: '0.1em' }}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: !isSmall ? 'flex' : 'none',
              alignItems: 'center',
            }}
          >
            <BrandLockup
              onClick={() => {
                void onChangeRoute(HOME_ROUTE)
              }}
              logoSize={40}
              textVariant="h6"
            />
          </Box>
          <Box
            sx={{
              display: !isSmall ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              ml: 'auto',
            }}
          >
            {navItems.map(({ title, path }) => {
              const isNavBtn = path !== HOME_ROUTE && path !== UNITS_ROUTE

              if (isNavBtn) {
                return (
                  <Button
                    sx={{
                      ...NAV_SX_PROP[path],
                      minWidth: { sm: 168 },
                    }}
                    onClick={async () => await onChangeRoute(path)}
                    key={title}
                    variant="contained"
                    color={NAV_COLOR_PROP[path]}
                    startIcon={NAV_ICON_PROP[path]}
                  >
                    {title}
                  </Button>
                )
              }
              return (
                <Button
                  onClick={async () => await onChangeRoute(path)}
                  key={title}
                  sx={{
                    color: (theme) =>
                      pathname === path
                        ? theme.palette.primary.dark
                        : theme.palette.text.primary,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 1.25,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: (theme: Theme) => theme.palette.primary.dark,
                    },
                  }}
                >
                  {title}
                </Button>
              )
            })}
          </Box>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          container={document.body}
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: isSmall ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background:
                'linear-gradient(180deg, rgba(15, 18, 23, 0.99) 0%, rgba(10, 13, 18, 1) 100%)',
              borderLeft: '1px solid rgba(173, 223, 255, 0.08)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  )
}
